<template>
  <!--
    WHAT — Structured Analysis detail tab "Prompt". Edits the four free-text
      agent-instruction fields: Normal definition, Output actions, Signal vs
      noise, and Proof requirements.

    CONTRACT — hydrates each field from record.config.{normal_definition,
      output_actions, signal_noise, proof_requirements}. On input emits
      patch({ config }) where config is a spread of record.config with all four
      keys written back. Mutates the ONE shared `config` blob (spread preserves
      unrelated config keys).

    NOTES — hydrating guard: `values` seeded in data(), emit() no-ops until
      $nextTick(mounted) clears `hydrating`. Plain textareas — these are free
      text, so no JSON validation (unlike the Primitives/Output tabs). Not
      selfChrome — ArtifactDetail wraps this body in the standard titled card.
  -->
  <!-- Structured Analysis "Prompt". Agent executor: four framing fields
       (config.{normal_definition,output_actions,signal_noise,proof_requirements}).
       Dossier executor: a LIST of {name, content} blocks (config.prompt_blocks),
       flattened to config.prompt (what the runner reads). {placeholder} tokens in
       block content are run_context-substituted at fire/test time. -->
  <div class="space-y-4">
    <!-- Dossier: the prompt is a LIST of blocks. Each block has a name +
         description, and is EITHER static content OR driven by a variable
         (whose value comes from run_context — the opp at fire time, or the Test
         form). A variable-driven block has no content; the value IS the content. -->
    <div v-if="isDossier" class="space-y-3">
      <div class="flex items-center justify-between">
        <label class="block text-[11px] font-medium text-gray-600">Prompt blocks</label>
        <button type="button" class="text-[11px] text-blue-600 hover:text-blue-800 font-medium" @click="addBlock">+ Add block</button>
      </div>
      <div v-if="!promptBlocks.length" class="text-[11px] text-gray-400 italic">No blocks yet — add one.</div>
      <div v-for="(blk, idx) in promptBlocks" :key="idx" class="border border-gray-200 rounded-lg p-3 bg-gray-50/50 space-y-2">
        <div class="flex gap-2 items-center">
          <input
            v-model="blk.name" type="text" placeholder="Block name (e.g. Instruction, Focus, Rules)"
            class="flex-1 text-xs px-2 py-1.5 border border-gray-200 rounded font-medium bg-white focus:outline-none focus:border-blue-400"
            @input="emit" />
          <button type="button" class="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded" @click="removeBlock(idx)">×</button>
        </div>
        <input
          v-model="blk.description" type="text" placeholder="Description (what this block is for — admin note, not sent to the model)"
          class="w-full text-[11px] px-2 py-1.5 border border-gray-200 rounded bg-white text-gray-500 focus:outline-none focus:border-blue-400"
          @input="emit" />

        <!-- Source toggle: this block is STATIC text or driven by a VARIABLE. -->
        <div class="inline-flex text-[10px] rounded-md border border-gray-200 overflow-hidden">
          <button type="button" class="px-2 py-0.5" :class="!isVarBlock(blk) ? 'bg-gray-800 text-white' : 'bg-white text-gray-500'" @click="setBlockMode(blk, 'content')">Static content</button>
          <button type="button" class="px-2 py-0.5 border-l border-gray-200" :class="isVarBlock(blk) ? 'bg-gray-800 text-white' : 'bg-white text-gray-500'" @click="setBlockMode(blk, 'variable')">Driven by variable</button>
        </div>
        <p v-if="isVarBlock(blk) && blk.content && blk.content.trim()" class="text-[10px] text-gray-400">
          The static text is kept; switch back to Static content to use it again.
        </p>

        <!-- STATIC -->
        <textarea
          v-if="!isVarBlock(blk)"
          v-model="blk.content" rows="5"
          class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded bg-white focus:outline-none focus:border-blue-400 resize-y leading-relaxed"
          placeholder="Block content…"
          @input="emit"
        ></textarea>

        <!-- VARIABLE-DRIVEN (no content — the value is the content) -->
        <div v-else>
          <input
            v-model="blk.variable" type="text" placeholder="run-context key (e.g. discharge_time)"
            class="w-full text-[11px] px-2 py-1.5 border border-gray-200 rounded font-mono bg-white focus:outline-none focus:border-blue-400"
            @input="emit" />
          <p class="text-[10px] text-gray-400 mt-1 leading-snug">
            This block's content is the value of
            <span class="font-mono text-gray-500">{{ (blk.variable || '').trim() || '(unset)' }}</span>,
            supplied from the opportunity at fire time (or the Test form).
          </p>
        </div>
      </div>
    </div>

    <!-- Agent: four framing fields. -->
    <template v-else>
      <div v-for="f in fields" :key="f.key">
        <label class="block text-[11px] font-medium text-gray-600 mb-1">{{ f.label }}</label>
        <textarea
          v-model="values[f.key]"
          :rows="f.rows || 3"
          class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400 resize-y leading-relaxed"
          :placeholder="f.placeholder"
          @input="emit"
        ></textarea>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'StructuredPromptSection',
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, required: true },
    context: { type: Object, default: () => ({}) },
  },
  emits: ['patch'],
  data() {
    const cfg = this.record.config || {};
    const fields = [
      { key: 'normal_definition', label: 'Normal definition', rows: 3, placeholder: 'What does "normal" look like for this population?' },
      { key: 'output_actions', label: 'Output actions', rows: 3, placeholder: 'What actions should the agent recommend?' },
      { key: 'signal_noise', label: 'Signal vs noise', rows: 3, placeholder: 'How to distinguish a real signal from noise?' },
      { key: 'proof_requirements', label: 'Proof requirements', rows: 3, placeholder: 'What evidence must the agent cite?' },
    ];
    const values = {};
    fields.forEach((f) => { values[f.key] = cfg[f.key] || ''; });
    // Prompt blocks: prefer structured config.prompt_blocks; else migrate an
    // existing flat config.prompt string into a single "Instruction" block.
    let promptBlocks = [];
    if (Array.isArray(cfg.prompt_blocks) && cfg.prompt_blocks.length) {
      promptBlocks = cfg.prompt_blocks.map((b) => ({
        name: b.name || '', description: b.description || '',
        content: b.content || '', variable: b.variable || '',
        mode: b.mode || ((b.variable || '').trim() ? 'variable' : 'content'),
      }));
    } else if (cfg.prompt) {
      promptBlocks = [{ name: 'Instruction', description: '', content: cfg.prompt, variable: '', mode: 'content' }];
    }
    return { fields, values, promptBlocks, hydrating: true };
  },
  computed: {
    isDossier() {
      return ((this.record.config || {}).executor) === 'patient_dossier';
    },
  },
  mounted() { this.$nextTick(() => { this.hydrating = false; }); },
  methods: {
    addBlock() { this.promptBlocks.push({ name: '', description: '', content: '', variable: '', mode: 'content' }); },
    removeBlock(idx) { this.promptBlocks.splice(idx, 1); this.emit(); },
    isVarBlock(b) { return b.mode ? b.mode === 'variable' : !!(b.variable && b.variable.trim()); },
    setBlockMode(b, mode) {
      if (b.mode === mode || (!b.mode && this.isVarBlock(b) === (mode === 'variable'))) return;
      b.mode = mode;
      if (mode === 'variable' && !(b.variable && b.variable.trim())) return;
      this.emit();
    },
    // Flatten blocks to the single prompt string the backend consumes. A STATIC
    // block renders "## <name>\n<content>". A VARIABLE block renders
    // "## <name>\n{<var>}" — the backend's run_context substitution replaces
    // {<var>} at fire/test time and the Test form auto-detects it. description
    // is an admin note only — NOT emitted to the model.
    flattenBlocks() {
      return this.promptBlocks
        .filter((b) => (b.name && b.name.trim()) || (b.content && b.content.trim()) || (b.variable && b.variable.trim()))
        .map((b) => {
          const nm = (b.name || '').trim();
          const v = this.isVarBlock(b) ? (b.variable || '').trim() : '';
          const body = v ? `{${v}}` : (b.content || '');
          return nm ? `## ${nm}\n${body}` : body;
        })
        .join('\n\n');
    },
    emit() {
      if (this.hydrating) return;
      const config = { ...(this.record.config || {}) };
      if (this.isDossier) {
        // Persist BOTH: structured blocks (editor source of truth) and the
        // flattened prompt string (what run_dossier_for_patient reads).
        config.prompt_blocks = this.promptBlocks.map((b) => ({
          name: b.name || '', description: b.description || '',
          content: b.content || '', variable: b.variable || '',
          mode: this.isVarBlock(b) ? 'variable' : 'content',
        }));
        config.prompt = this.flattenBlocks();
      } else {
        for (const f of this.fields) config[f.key] = this.values[f.key];
      }
      this.$emit('patch', { config });
    },
  },
};
</script>
